import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { faFilter } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, Button, Empty, Input, Popover, Radio, Select, Table } from 'antd';
import { Buffer } from 'buffer';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { getCrsCustomerListData } from '../../../../redux/actions/crs/crsListView';
import { NetworkLayer } from '../../../../services/crs/crsListView';
import CustomerFilter from '../CustomerFilter';
import style from './index.scss';
import {
  parseColumnsForCustomerListing,
  parseCsvHeadersForCustomerListing
} from './util';
import Wait4Me from '../../../../components/Wait4Me';
import classnames from 'classnames';
import { capitalize, toLower } from 'lodash';
import useAlertLevelConfigs from '../../../common/hooks/useAlertLevelConfig';

const api = new NetworkLayer();

const CustomerListView = ({
  customerListData,
  segmentListData,
  clusterListData,
  customerTypeListData,
  riskLevelChangeListData,
  dispatch,
  GSConfig,
  alertLevels
}) => {
  const { isLoading, data } = customerListData;
  const { data: customerList, count } = data;
  const [downloadableData, setDownloadableData] = useState([]);
  const csvLink = useRef();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [importanceFilter, setImportanceFilter] = useState('all');
  const [filters, setFilters] = useState([
    {
      itemName: 'placeholder-0'
    }
  ]);
  const [csvDownloadClicked, setCsvDownloadClicked] = useState(false);
  const [sortBy, setSortBy] = useState('customerOnboardDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const { crsAlertLevels, getCRSLevelColor } =
    useAlertLevelConfigs(alertLevels);

  const nonEmptyFilters = filters.filter(
    (f) => Array.isArray(f.values) && f.values.length > 0
  );

  const getPayload = (page, pageSize, filtersEnabled = true) => {
    const payload = {
      limit: pageSize,
      offset: page * pageSize,
      sortBy,
      sortOrder
    };

    if (searchText.trim()) payload.searchText = searchText;
    if (importanceFilter !== 'all') payload.riskLevel = importanceFilter;

    // parse the filters from popover
    if (filtersEnabled) {
      const filtersRaw = nonEmptyFilters.map((f) => ({
        key: f.itemName,
        operator: '=',
        value: f.values[0] // currently the backend only supports single value filtering
      }));

      const customerName = filtersRaw.find((f) => f.key === 'customerName');
      if (customerName) payload.searchText = customerName.value;

      // clusterId filter - pass to API
      const clusterId = filtersRaw.find((f) => f.key === 'clusterId');
      if (clusterId) payload.clusterId = clusterId.value;

      const segmentCode = filtersRaw.find((f) => f.key === 'segmentCode');
      if (segmentCode) payload.segmentId = segmentCode.value;

      // customerType is removed from filters. we keep it here for future API integration
      // others fields like customerId, customerType, and riskShift should be encoded and pass to filters param
      const otherFilters = filtersRaw.filter(
        (f) => !['customerName', 'clusterId', 'segmentCode'].includes(f.key)
      );
      if (otherFilters.length > 0) {
        const filtersString = JSON.stringify(otherFilters);
        const filtersEncoded = Buffer.from(filtersString).toString('base64');
        payload.filters = filtersEncoded;
      }
    }

    return payload;
  };

  const fetchData = (page, pageSize, filtersEnabled = true) => {
    const payload = getPayload(page, pageSize, filtersEnabled);
    dispatch(getCrsCustomerListData(payload));
  };

  const onPaginationChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
    fetchData(page - 1, pageSize);
  };

  const onImportanceFilterChange = (e) => {
    setImportanceFilter(e.target.value);
    setCurrentPage(1);
  };

  const onSearchPressEnter = () => {
    fetchData(0, pageSize);
    setCurrentPage(1);
  };

  const handleApplyButtonClick = () => {
    fetchData(0, pageSize);
    setCurrentPage(1);
    setFilterPopoverOpen(false);
  };

  const handleClearButtonClick = () => {
    setFilters([{ itemName: 'placeholder-0' }]);
    fetchData(0, pageSize, false);
    setCurrentPage(1);
  };

  const downloadData = async () => {
    setCsvDownloadClicked(true);
    const payload = getPayload(0, 5000);
    payload.isDownload = true;
    const downloadable = await api.customerListAPI(payload);
    setDownloadableData(downloadable.data);
    csvLink.current.link.click();
    setCsvDownloadClicked(false);
  };

  useEffect(() => {
    fetchData(currentPage - 1, pageSize);
  }, [importanceFilter]);

  // Refetch when sort changes
  useEffect(() => {
    fetchData(0, pageSize);
    setCurrentPage(1);
  }, [sortBy, sortOrder]);

  const handleSortByChange = (value) => {
    setSortBy(value);
  };

  const handleSortOrderChange = (value) => {
    setSortOrder(value);
  };

  const columns = parseColumnsForCustomerListing(
    GSConfig && GSConfig.widgetConfig,
    getCRSLevelColor
  );

  const csvHeaders = parseCsvHeadersForCustomerListing(
    GSConfig && GSConfig.widgetConfig
  );

  return (
    <div>
      <div className={style.titleBar}>
        <div className={style.title}>Customer Risk List</div>
        <Button
          type="text"
          icon={
            csvDownloadClicked ? (
              <div className={style.loaderContainer}>
                <Wait4Me />
              </div>
            ) : (
              <i className={classnames('icon20', 'icon20_icon_download')} />
            )
          }
          onClick={downloadData}
          disabled={csvDownloadClicked}
        />
        <CSVLink
          ref={csvLink}
          data={downloadableData}
          headers={csvHeaders}
          filename="customer-risk-list.csv"
        />
      </div>
      <div className={style.toolBar}>
        <div className={style.searchInput}>
          <label className={style.label}>Search</label>
          <Input
            size="small"
            value={searchText}
            placeholder="Search"
            prefix={<SearchOutlined />}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={onSearchPressEnter}
          />
        </div>
        <div className={style.importanceRadio}>
          <label className={style.label}>Importance</label>
          <Radio.Group
            style={{ display: 'block' }}
            size="small"
            value={importanceFilter}
            onChange={onImportanceFilterChange}>
            <Radio.Button value="all">All</Radio.Button>
            {crsAlertLevels?.map(({ code, displayName, color }) => (
              <Radio.Button key={code} value={toLower(code)}>
                <div
                  className={style.importanceIndicator}
                  style={{ background: color }}
                />
                {displayName || capitalize(code)}
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
        <div className={style.filterButton}>
          <label className={style.label}>Filter</label>
          <Badge
            size="small"
            count={filterPopoverOpen ? 0 : nonEmptyFilters.length}
            style={{ backgroundColor: '#21B4BD' }}>
            <Popover
              placement="bottomLeft"
              title={
                <>
                  <header>Filter</header>
                  <Button
                    size="small"
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={() => setFilterPopoverOpen(false)}
                  />
                </>
              }
              content={
                <CustomerFilter
                  segmentListData={segmentListData}
                  clusterListData={clusterListData}
                  customerTypeListData={customerTypeListData}
                  riskLevelChangeListData={riskLevelChangeListData}
                  filters={filters}
                  setFilters={setFilters}
                  handleApplyButtonClick={handleApplyButtonClick}
                  handleClearButtonClick={handleClearButtonClick}
                />
              }
              visible={filterPopoverOpen}
              trigger="click"
              onVisibleChange={(visible) => setFilterPopoverOpen(visible)}>
              <Button size="small" icon={<FontAwesomeIcon icon={faFilter} />} />
            </Popover>
          </Badge>
        </div>
        <div className={style.sortControls}>
          <label className={style.label}>Sort By</label>
          <Select
            size="small"
            value={sortBy}
            onChange={handleSortByChange}
            style={{ width: 120 }}
            options={[
              { label: 'Risk Score', value: 'overallRiskScore' },
              { label: 'Customer ID', value: 'customerKey' },
              { label: 'Segment', value: 'segmentCode' },
              { label: 'Date', value: 'customerOnboardDate' }
            ]}
          />
          <Select
            size="small"
            value={sortOrder}
            onChange={handleSortOrderChange}
            style={{ width: 100, marginLeft: 8 }}
            options={[
              { label: 'Desc', value: 'desc' },
              { label: 'Asc', value: 'asc' }
            ]}
          />
        </div>
      </div>
      <Table
        className={style.table}
        dataSource={customerList}
        columns={columns}
        loading={isLoading}
        rowKey={(record) => record.customerKey}
        pagination={{
          total: count,
          current: currentPage,
          pageSize,
          onChange: onPaginationChange,
          showTotal: (total) => `${total} Records`
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No results found"
            />
          )
        }}
      />
    </div>
  );
};

CustomerListView.propTypes = {
  customerListData: PropTypes.any,
  segmentListData: PropTypes.any,
  clusterListData: PropTypes.any,
  customerTypeListData: PropTypes.any,
  riskLevelChangeListData: PropTypes.any,
  dispatch: PropTypes.func,
  GSConfig: PropTypes.object,
  alertLevels: PropTypes.array
};

export default CustomerListView;
